import { readAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useEffect } from "react";
import { useHistory } from "react-router";
import { useWorkspaceDispatch, useWorkspaceSlices } from "../state";
import { viewModeFromTabParam } from "../view-tab-param";

/**
 * Keep the optional `tab` search param and the workspace's `view` slice in agreement, in both
 * directions:
 *
 * - **param → view**: a `tab` that disagrees with the visible view switches it. This is what keeps
 *   the tabs addressable once the page is already mounted — a navigation to the same workspace with
 *   a different `tab` (what `open_page` does) only changes the search string, so nothing remounts
 *   and the initial seed in `WorkspacePageInner` never runs again. Dispatched with `persist: false`:
 *   landing on a tab shows it, it does not rewrite the workspace's saved `viewMode` the way clicking
 *   the tab does.
 * - **view → param**: every view change is mirrored back into the URL with a `replace`, whatever
 *   triggered it (the toolbar tabs, the chart's empty state, the table's "see in chart"), so the
 *   param can't end up contradicting what's on screen and the link stays copy-pasteable.
 */
export function useWorkspaceViewTabParam(tab: string | undefined): void {
	const slices = useWorkspaceSlices();
	const dispatch = useWorkspaceDispatch();
	const history = useHistory();
	const view = useAtomValue(slices.view$);
	const requestedView = viewModeFromTabParam(tab);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <only a newly requested tab may switch the view>
	useEffect(() => {
		if (requestedView && requestedView !== readAtom(slices.view$)) {
			void dispatch({ type: "setView", view: requestedView, persist: false });
		}
	}, [requestedView]);

	useEffect(() => {
		const search = new URLSearchParams(history.location.search);
		if (search.get("tab") === view) {
			return;
		}
		search.set("tab", view);
		history.replace({ ...history.location, search: search.toString() });
	}, [view, history]);
}
